import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Folder,
  FolderOpen,
  FolderPlus,
  Inbox,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import {
  listMediaFolders,
  createMediaFolder,
  updateMediaFolder,
  deleteMediaFolder,
  MediaFolder,
  MediaFolderType,
} from '../../services/mediaFolderService';

interface Props {
  // Tipo dos itens (image | video). Backend retorna pastas type=mixed + type=match.
  mediaType: 'image' | 'video';
  // null = "Todas" (sem filtro), 'none' = "Sem pasta" (folder_id=null), string = pasta específica
  selectedFolderId: string | null | 'none';
  onSelect: (folderId: string | null | 'none') => void;
  // Quando algum item é largado em uma pasta. Receivers implementam o move via API.
  onDropOnFolder?: (folderId: string | null) => void;
  // Quantidade total exibida ao lado de "Todas" (opcional)
  totalCount?: number;
  // Variante compacta pra uso em modal-selector
  compact?: boolean;
}

const FolderSidebar: React.FC<Props> = ({
  mediaType,
  selectedFolderId,
  onSelect,
  onDropOnFolder,
  totalCount,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creatingName, setCreatingName] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [dragOverId, setDragOverId] = useState<string | null | 'none'>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listMediaFolders(mediaType);
      setFolders(res.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('mediaFolders.loadError', 'Erro ao carregar pastas'));
    } finally {
      setIsLoading(false);
    }
  }, [mediaType, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async () => {
    const name = (creatingName || '').trim();
    if (!name) {
      setCreatingName(null);
      return;
    }
    try {
      const folderType: MediaFolderType = mediaType === 'image' ? 'image' : 'video';
      await createMediaFolder({ name, type: folderType });
      toast.success(t('mediaFolders.created', 'Pasta criada'));
      setCreatingName(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('mediaFolders.saveError', 'Erro ao criar pasta'));
    }
  };

  const handleRename = async (id: string) => {
    const name = editingName.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    try {
      await updateMediaFolder(id, { name });
      setEditingId(null);
      setFolders(prev => prev.map(f => f.id === id ? { ...f, name } : f));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('mediaFolders.saveError', 'Erro ao renomear pasta'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('mediaFolders.confirmDelete', 'Remover essa pasta? Os itens dentro voltam pra raiz.') as string)) return;
    try {
      await deleteMediaFolder(id);
      if (selectedFolderId === id) onSelect(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('mediaFolders.deleteError', 'Erro ao remover pasta'));
    }
  };

  // Drag handlers — só processam se o consumidor passou onDropOnFolder
  const handleDragOver = (e: React.DragEvent, folderId: string | null | 'none') => {
    if (!onDropOnFolder) return;
    // Aceita drop apenas se o payload tem o tipo certo
    const types = Array.from(e.dataTransfer.types || []);
    if (!types.includes('application/x-media-id') && !types.includes('text/plain')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(folderId);
  };
  const handleDragLeave = () => setDragOverId(null);
  const handleDrop = (e: React.DragEvent, folderId: string | null) => {
    if (!onDropOnFolder) return;
    e.preventDefault();
    setDragOverId(null);
    onDropOnFolder(folderId);
  };

  const renderCountBadge = (folder: MediaFolder) => {
    const count = mediaType === 'image' ? folder.image_count : folder.video_count;
    if (!count) return null;
    return <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{count}</span>;
  };

  const itemBase = 'group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors min-w-0';
  const itemSelected = 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300';
  const itemNormal = 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800';
  const itemDragOver = 'ring-2 ring-primary-400 bg-primary-50 dark:bg-primary-900/40';

  return (
    <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-2'}`}>
      {/* "Todas" — sem filtro */}
      <div
        onClick={() => onSelect(null)}
        onDragOver={onDropOnFolder ? (e) => handleDragOver(e, null) : undefined}
        onDragLeave={handleDragLeave}
        onDrop={onDropOnFolder ? (e) => handleDrop(e, null) : undefined}
        className={`${itemBase} ${selectedFolderId === null ? itemSelected : itemNormal} ${dragOverId === null && onDropOnFolder ? itemDragOver : ''}`}
      >
        <Inbox className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 truncate">{t('mediaFolders.all', 'Todas')}</span>
        {totalCount !== undefined && <span className="text-xs text-gray-500 tabular-nums">{totalCount}</span>}
      </div>

      {/* "Sem pasta" — itens sem folder atribuído */}
      <div
        onClick={() => onSelect('none')}
        className={`${itemBase} ${selectedFolderId === 'none' ? itemSelected : itemNormal}`}
      >
        <Folder className="w-4 h-4 flex-shrink-0 text-gray-400" />
        <span className="flex-1 truncate">{t('mediaFolders.unfiled', 'Sem pasta')}</span>
      </div>

      {/* Divider + label */}
      {!compact && (
        <div className="flex items-center justify-between mt-2 px-2">
          <span className="text-xs uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
            {t('mediaFolders.title', 'Pastas')}
          </span>
          <button
            type="button"
            onClick={() => setCreatingName('')}
            title={t('mediaFolders.new', 'Nova pasta') as string}
            className="p-1 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <FolderPlus className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Linha de criação inline */}
      {creatingName !== null && (
        <div className="flex items-center gap-1 px-1 min-w-0">
          <input
            autoFocus
            value={creatingName}
            onChange={(e) => setCreatingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
              if (e.key === 'Escape') { e.preventDefault(); setCreatingName(null); }
            }}
            placeholder={t('mediaFolders.namePlaceholder', 'Nome da pasta') as string}
            maxLength={120}
            className="input flex-1 min-w-0 w-0 text-sm py-1"
          />
          <button
            type="button"
            onClick={handleCreate}
            className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setCreatingName(null)}
            className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Lista de pastas */}
      {isLoading ? (
        <div className="px-2 py-1 text-xs text-gray-500 inline-flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('common.loading', 'Carregando...')}
        </div>
      ) : folders.length === 0 && creatingName === null ? (
        <p className="px-2 py-1 text-xs text-gray-500 italic">
          {t('mediaFolders.empty', 'Nenhuma pasta. Crie a primeira.')}
        </p>
      ) : (
        folders.map((f) => {
          const isSelected = selectedFolderId === f.id;
          const isEditing = editingId === f.id;
          const isDragOver = dragOverId === f.id;
          return (
            <div
              key={f.id}
              onClick={() => !isEditing && onSelect(f.id)}
              onDragOver={onDropOnFolder ? (e) => handleDragOver(e, f.id) : undefined}
              onDragLeave={handleDragLeave}
              onDrop={onDropOnFolder ? (e) => handleDrop(e, f.id) : undefined}
              className={`${itemBase} ${isSelected ? itemSelected : itemNormal} ${isDragOver ? itemDragOver : ''}`}
            >
              {isSelected ? (
                <FolderOpen className="w-4 h-4 flex-shrink-0" style={f.color ? { color: f.color } : undefined} />
              ) : (
                <Folder className="w-4 h-4 flex-shrink-0" style={f.color ? { color: f.color } : undefined} />
              )}
              {isEditing ? (
                <input
                  autoFocus
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleRename(f.id); }
                    if (e.key === 'Escape') { e.preventDefault(); setEditingId(null); }
                  }}
                  onBlur={() => handleRename(f.id)}
                  maxLength={120}
                  className="flex-1 min-w-0 w-0 bg-transparent border border-gray-300 dark:border-gray-700 rounded px-1 py-0.5 text-sm"
                />
              ) : (
                <>
                  <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                  {renderCountBadge(f)}
                  {!compact && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(f.id);
                          setEditingName(f.name);
                        }}
                        className="p-1 text-gray-400 hover:text-primary-600"
                        title={t('common.rename', 'Renomear') as string}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title={t('common.remove', 'Remover') as string}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default FolderSidebar;
